import { useCallback, useEffect, useRef, useState } from "react";
import Header from "../../common/Header/Header";
import { Page, TableWrapper, Buttons, ButtonsSeparator } from "../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles";
import Button from "../../components/Button/Button";
import { Variant } from "../../components/Button/Button.types";
import Table from "../../components/Table/Table";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DeleteButton, InputWrapper, ReadOnlyInputs, StyledButtons } from "./RawProdGins.style";
import { CellTextWrapper, EditCell, IconButton } from "../../components/Table/EditCellRenderer/EditCellRenderer.styles";
import { ICellRendererParams, SelectionChangedEvent } from "ag-grid-community";
import { useTheme } from "@emotion/react";
import { AxiosError } from 'axios';
import Modal from "../../components/Modal/Modal";
import Input from "../../components/Input/Input";
import { useRawProdGins, useRawProdGinsUploadFile, useDeleteRawProdGins, useAddNewRawProdGins, useEditRawProdGin } from "../../services/rawProd/rawProd.service";
import { showToast } from "../../components/Toast/Toast";
import { genericErrorShowToastOptions } from "../../utils/useGenericErrorMessage/useGenericErrorMessage";
import { ToastTypes } from "../../components/Toast/Toast.types";
import useNavigateWithToast from "../../utils/useNavigateWithToast/useNavigateWithToast";

const RawProdGINs = () => {
    const theme = useTheme();
    const navigateWithToast = useNavigateWithToast();

    const { data: rawProdsGinsData, isError: rawProdsGinsError, refetch } = useRawProdGins(true);
    const rawProdGins = rawProdsGinsData?.data;
    const { mutate: editRawProdGin, isError: isEditRawProdGinError, error: editError } = useEditRawProdGin();

    const { mutate, isError: rawProdGinsUploadFileError } = useRawProdGinsUploadFile();
    const { mutate: deleteRawProdGin, isLoading: isDeleteLoading, isError: isDeleteError, error: deleteError } = useDeleteRawProdGins();
    const { mutate: addNewRawProdGin, isError: isAddError, isLoading: isAddLoading, error: addError } = useAddNewRawProdGins();
    const [addNewGinNo, setAddNewGinNo] = useState<number>();
    const [addNewGinDescription, setAddNewGinDescription] = useState<string>();
    const [addNewConversionFactor, setAddNewConversionFactor] = useState<number>()

    // Ref for GIN input field autofocus
    const ginInputRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        if (isEditRawProdGinError) {
            closeModal();
            if ((editError as AxiosError).message === 'apiGatewayTimeout') return;
            showToast({ ...genericErrorShowToastOptions, label: 'Error editing Raw Prod Gin' });
        }
    }, [isEditRawProdGinError]);

    useEffect(() => {
        if (isDeleteError) {
            closeModal();
            if ((deleteError as AxiosError).message === 'apiGatewayTimeout') return;
            showToast({ ...genericErrorShowToastOptions, label: 'Error deleting Raw Prod Gin' });
        }
    }, [isDeleteError]);

    useEffect(() => {
        if (isAddError) {
            closeModal();
            if ((addError as AxiosError).message === 'apiGatewayTimeout') return;
            showToast({ ...genericErrorShowToastOptions, label: 'Error adding Raw Prod Gin' });
        }
    }, [isAddError]);


    const [selectedRawProdGin, setSelectedRawProdGin] = useState<any>();
    const [modalOpen, setModalOpen] = useState(false);
    const [modalAddNewOpen, setModalAddNewOpen] = useState(false);

    useEffect(() => {
        if (rawProdGinsUploadFileError) {
            showToast({
                ...genericErrorShowToastOptions,
                label: 'Error uploading file',
                id: 'rawProdGinsUploadFileError',
                toastType: ToastTypes.Error,
            });
        }
    }, [rawProdGinsUploadFileError])
    useEffect(() => {
        if (rawProdsGinsError)
            showToast({
                ...genericErrorShowToastOptions,
                label: 'Error fetching raw products.',
                id: 'optionsRawProdsFetchError',
                toastType: ToastTypes.Error,
            });
    }, [rawProdsGinsError]);
    const closeModal = () => {
        setModalOpen(false);
    };
    const openModal = () => {
        setModalOpen(true);
    };
    const openAddNewModal = () => {
        setModalAddNewOpen(true);
    };

    const closeAddNewModal = () => {
        setModalAddNewOpen(false);
        // Clear form values when closing the modal
        setAddNewGinNo(undefined);
        setAddNewGinDescription(undefined);
        setAddNewConversionFactor(undefined);
    };

    // Focus the GIN input field when the add new modal opens for accessibility
    useEffect(() => {
        if (modalAddNewOpen) {
            // Use setTimeout to ensure the modal is fully rendered before focusing
            const timer = setTimeout(() => {
                if (ginInputRef.current) {
                    ginInputRef.current.focus();
                }
            }, 150);
            
            return () => clearTimeout(timer);
        }
    }, [modalAddNewOpen]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const formData = new FormData();
            const file = event.target.files[0];
            formData.append('file', file);
            //validate if the file is les than 5mb
            if (file.size < 5_000_000) {
                mutate(
                    formData,
                    {
                        onSuccess: () => {
                            navigateWithToast('/tables/rawprodgins', {
                                label: 'Raw Prod Gin has been imported.',
                                id: 'rawProdGinImportSuccess',
                            });
                            refetch();
                        }
                    }
                )
            }

        }

    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const onSelectionChanged = (event: SelectionChangedEvent) => {
        setSelectedRawProdGin(event.api.getSelectedRows());
    }

    const handleCellEdit = useCallback(        (event: any) => {
            let newValue = +event.newValue;
            if (Number.isNaN(newValue)) newValue = event.oldValue;
            if (newValue === event.oldValue) {
                showToast({
                    label: 'Please input a numerical value.',
                    id: 'conversionFactorError',
                    toastType: ToastTypes.Error,
                });
            } else {
                editRawProdGin(
                    {
                        ginID: event.data.ginID,
                        conversionFactor: newValue
                    },
                    {
                        onSuccess: () => {
                            showToast({
                                label: 'Conversion Factor updated successfully.',
                                id: 'conversionFactorSuccess',
                            });
                            refetch();
                        },
                        onError: (error) => {
                            showToast({
                                label: 'Error updating conversion factor.',
                                id: 'conversionFactorError',
                                toastType: ToastTypes.Error,
                            });
                        },
                    }
                )
            }
        }, [selectedRawProdGin]
    );

    const EditConvFactor = (props: ICellRendererParams) => {
        return (
            <EditCell>
                <CellTextWrapper>{props.value}</CellTextWrapper>
                <IconButton aria-label='edit'>
                    <EditOutlinedIcon htmlColor={theme.colors.blue} />
                </IconButton>
                <></>
            </EditCell>
        );
    };


    const RemoveRowData = () => {
        return (
            <EditCell>
                <DeleteButton>
                    <DeleteOutlinedIcon
                        fontSize='inherit'
                        htmlColor={theme.colors.red}
                        style={{ cursor: 'pointer', height: '100%' }}
                        onClick={openModal}
                    />
                </DeleteButton>
            </EditCell>
        );
    };

    const onRemoveRow = () => {
        if (selectedRawProdGin) {
            deleteRawProdGin(selectedRawProdGin[0].ginID,
                {
                    onSuccess: () => {
                        navigateWithToast('/tables/rawprodgins', {
                            label: 'Raw Prod Gin has been deleted.',
                            id: 'rawProdGinDeleteSuccess',
                        });
                        closeModal();
                        refetch();
                        setSelectedRawProdGin([])
                    }
                }
            );
        }

    }

    const handleAddNewRawProdGin = () => {
        if (selectedRawProdGin) {
            addNewRawProdGin(
                {
                    ginID: selectedRawProdGin[0].ginID,
                    conversionFactor: addNewConversionFactor as number,
                    createdBy: selectedRawProdGin[0].createdBy,
                    ginNo: addNewGinNo as number,
                    ginRawProdNo: selectedRawProdGin[0].rawProdNo,
                    description: addNewGinDescription,
                    accountCode: selectedRawProdGin[0].accountCode,
                    ginCase: selectedRawProdGin[0].ginCase
                },
                {
                    onSuccess: () => {
                        navigateWithToast('/tables/rawprodgins', {
                            label: 'Raw Prod Gin has been created',
                            id: 'rawProdGinCreateSuccess',
                        });
                        closeAddNewModal();
                        refetch();
                        setSelectedRawProdGin([]);
                    }
                }
            );

        }
    }

    return (
        <>
            <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Raw Prod GINs' titleTestId={"RawProdGinsPageTitle"} />
            <Page>
                <TableWrapper>
                    <Table
                        onSelectionChanged={onSelectionChanged}
                        returnedRecords={rawProdGins?.length || 0}
                        rowData={rawProdGins}
                        onCellEditRequest={handleCellEdit}
                        height={'100%'}
                        rowSelection='single'
                        readOnlyEdit={true}
                        columnDefs={[
                            {
                                minWidth: 70,
                                field: '',
                                maxWidth: 70,
                                headerName: 'Select',
                                checkboxSelection: true,
                                floatingFilter: false,
                                sortable: false,
                            },
                            { minWidth: 112, field: 'rawProdNo', headerName: 'MMS Raw Product No.' },
                            { minWidth: 112, field: 'moaRawProdNo', headerName: 'MOA Raw Product No.' },
                            { minWidth: 112, field: 'ginCase', headerName: 'GIN Case' },
                            { minWidth: 112, field: 'ginNo', headerName: 'GIN No.' },
                            { minWidth: 112, field: 'ginDescription', headerName: 'GIN description' },
                            { minWidth: 112, field: 'conversionFactor', headerName: 'Conversion Factor', editable: true, singleClickEdit: false, cellRenderer: EditConvFactor },
                            {
                                minWidth: 70,
                                maxWidth: 70,
                                flex: 1,
                                field: 'remove',
                                headerName: 'Delete',
                                resizable: false,
                                cellRenderer: RemoveRowData,
                                floatingFilter: false,
                            },
                        ]}
                    />
                </TableWrapper>
                <Buttons>
                    <Button
                        label='Add'
                        variant={Variant.Primary}
                        testId="AddRawProdGinButton"
                        disabled={selectedRawProdGin === undefined || selectedRawProdGin.length === 0}
                        onClick={openAddNewModal}
                    />
                    <ButtonsSeparator />
                    <input
                        type="file"
                        accept=".csv"
                        data-testid='uploadFileInput'
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button testId="ImportCSVButton" onClick={handleImportClick} label='Import' variant={Variant.Secondary} />
                </Buttons>
                <Modal id='removeRawProdGinModal' open={modalOpen} onClose={closeModal} title='Remove Raw Prod Gin'>
                    <div>
                        <ReadOnlyInputs>
                            <Input
                                label="MMSRawProdNo"
                                disabled
                                id='mmsrawprodno'
                                value={selectedRawProdGin ? selectedRawProdGin[0]?.rawProdNo : ''}
                            />
                            <Input
                                label='MOA RawProd No'
                                disabled
                                id='rawprodno'
                                value={selectedRawProdGin ? selectedRawProdGin[0]?.moaRawProdNo : ''}
                            />

                        </ReadOnlyInputs>
                        <ReadOnlyInputs>
                            <Input
                                label="GIN"
                                disabled
                                id='ginNo'
                                value={selectedRawProdGin ? selectedRawProdGin[0]?.ginNo : ''}
                            />
                            <Input
                                label='Gin Description'
                                disabled
                                id='ginDescription'
                                value={selectedRawProdGin ? selectedRawProdGin[0]?.ginDescription : ''}
                            />
                            <Input
                                label='Conversion Factor'
                                disabled
                                id='conversionFactor'
                                value={selectedRawProdGin ? selectedRawProdGin[0]?.conversionFactor : ''}
                            />
                        </ReadOnlyInputs>
                        <StyledButtons>
                            <Button label='Cancel' testId='closeModalButton' onClick={closeModal} variant={Variant.Secondary} />
                            <Button label='Delete' testId="removeRawProdGinButton" onClick={onRemoveRow} variant={Variant.Primary} isLoading={isDeleteLoading} />
                        </StyledButtons>
                    </div>
                </Modal>
                <Modal id='addNewRawProdGinModal' open={modalAddNewOpen} onClose={closeAddNewModal} title='Add New Raw Prod Gin'>
                    <div>
                        <InputWrapper>
                            <ReadOnlyInputs>
                                <Input
                                    label='MMSRawProdNo'
                                    disabled
                                    id='rawProdNo'
                                    value={selectedRawProdGin ? selectedRawProdGin[0]?.rawProdNo.toString() : ''}
                                />
                                <Input
                                    label='RawProdNo'
                                    disabled
                                    id='moaRawProdNo'
                                    value={selectedRawProdGin ? selectedRawProdGin[0]?.moaRawProdNo : ''}
                                />
                                <Input
                                    label='GIN Case'
                                    disabled
                                    id='ginCase'
                                    value={selectedRawProdGin ? selectedRawProdGin[0]?.ginCase : ''}
                                />
                            </ReadOnlyInputs>
                            <ReadOnlyInputs>                                <Input
                                    label='GIN'
                                    id='ginNo'
                                    testId='GinNoInput'
                                    value={addNewGinNo?.toString() || ''}
                                    onChange={(e) => setAddNewGinNo(Number(e.target.value) || undefined)}
                                    ref={ginInputRef}
                                />
                                <Input
                                    label='Gin Description'
                                    id='ginDescription'
                                    testId='GinDescriptionInput'
                                    value={addNewGinDescription || ''}
                                    onChange={(e) => { setAddNewGinDescription(e.target.value || undefined) }}
                                />
                                <Input
                                    label='Conversion Factor'
                                    id='conversionFactor'
                                    testId='ConversionFactorInput'
                                    value={addNewConversionFactor?.toString() || ''}
                                    onChange={(e) => { setAddNewConversionFactor(Number(e.target.value) || undefined) }}
                                />
                            </ReadOnlyInputs>
                            <StyledButtons>
                                <Button label='Cancel' testId='closeModalButton' onClick={closeAddNewModal} variant={Variant.Secondary} />
                                <Button label='Add' testId="AddNewRawProdGinModalButton" onClick={handleAddNewRawProdGin} variant={Variant.Primary} isLoading={isAddLoading} />
                            </StyledButtons>
                        </InputWrapper>
                    </div>
                </Modal>
            </Page>
        </>
    )
};

export default RawProdGINs;